import React from 'react'
import {
  Badge,
  Container,
  Link,
  ListContainer,
  Score,
  Select,
  TextTitle,
  TextCaption,
  TextBody,
  HtmlContent
} from '@pmi/pmi-iqos-ds-react'
import {
  analytics,
  goTo,
  getDynamicPath,
  Routes,
  LazyImage,
  Price,
  htmlTranslation
} from '@pmi/pmi-iqos-shared-react'

const ProductItem = ({
  item,
  noDetailLink,
  translations,
  onRemoveItem,
  onQuantityChange,
  onOptionChange,
  showFamily
}) => {
  const handleRemove = () => {
    if (onRemoveItem) {
      onRemoveItem(item)
    }
  }

  const handleQuantityChange = e => {
    if (onQuantityChange) {
      onQuantityChange(item, e.target.value)
    }
  }

  const handleOptionListChange = e => {
    if (onOptionChange) {
      if (item.isSample) {
        analytics.samplingCart('Click', 'Flavor dropdown')
      }
      onOptionChange(item, e.target.value)
    }
  }
  const Component = ({ isMobile, children }) => {
    return isMobile ? (
      <TextCaption size={TextCaption.sizes.m}>{children}</TextCaption>
    ) : (
      <TextTitle heading={TextTitle.headings.h5}>{children}</TextTitle>
    )
  }

  const renderProductItemName = isMobile => {
    return (
      <Container
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.row}
        justifyContent={Container.justifyContentValues.between}>
        <Container fullWidth>
          {item.isSample && !item.isSampleConfirmed ? (
            <Component isMobile={isMobile}>
              {translations.tk_sampleSelectTitle}
            </Component>
          ) : (
            <Container
              pb="4"
              onClickHandler={
                noDetailLink
                  ? null
                  : () => {
                      goTo(
                        getDynamicPath(Routes.products, item.id),
                        false,
                        false,
                        [`variantid=${selectedVariantId}`]
                      )
                    }
              }
              data-testing="Cart_Overlay_GoToProductDetail">
              <Component isMobile={isMobile}>
                {(item.isSample
                  ? translations.tk_sampleSelectTitlePrefix
                  : '') + item.name}
              </Component>
            </Container>
          )}
        </Container>
        {!item.chainId && (
          <Container pl="2" width={['5/12', '4/12', '3/12']}>
            {!item.isSample ? (
              <Component isMobile={isMobile}>
                <Price
                  size=""
                  listPrice={item.displayDiscount ? item.basePrice : null}
                  price={
                    item.displayDiscount
                      ? item.basePrice - item.discount
                      : item.basePrice
                  }
                  listPriceLabel={translations.tk_listPriceDescription}
                  priceAlignmentDisplay={Price.displays.block}
                  data-testing="Cart_Price"
                />
              </Component>
            ) : (
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.end}>
                {translations.tk_sampleFree}
              </Container>
            )}
          </Container>
        )}
      </Container>
    )
  }

  const renderPromotionBanner = isMobile => {
    return (
      !!item.promotionBanners.length &&
      item.promotionBanners.filter(Boolean).map((promotionBanner, index) => (
        <Container
          display={
            isMobile
              ? [
                  Container.displays.block,
                  Container.displays.none,
                  Container.displays.none
                ]
              : [
                  Container.displays.none,
                  Container.displays.block,
                  Container.displays.block
                ]
          }
          pt="3"
          key={index}>
          <HtmlContent content={promotionBanner} />
        </Container>
      ))
    )
  }

  const selectedVariant =
    item.selectedOptionIndex !== '' &&
    item.options.length > 0 &&
    item.selectedOptionIndex < item.options.length
      ? item.options[item.selectedOptionIndex]
      : null
  const selectedVariantId = selectedVariant ? selectedVariant.variantId : null
  const canBeRemoved = !!onRemoveItem
  const canQuantityBeChanged = !!onQuantityChange
  const canOptionBeChanged = !!onOptionChange
  const selectVariantItems = item.options.map((option, index) => ({
    label: `${option.shortVariantName} ${
      option.stock < 1 ? translations.tk_optionOutOfStock : ''
    }`,
    value: index,
    disabled: option.stock < 1
  }))
  if (item.isSample && !item.isSampleConfirmed) {
    selectVariantItems.unshift({
      label: translations.tk_sampleSelectListSelect,
      value: ''
    })
  }

  return (
    <ListContainer columns={1} yBetween="4">
      <Container
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.column}
        pb={['8', '10']}>
        {item.family && showFamily && (
          <Container pb={['3', '4']}>
            <Badge
              text={item.family.name}
              color={item.family.colour}
              border
              data-testing="Cart_Badge_ProductFamily"
            />
          </Container>
        )}
        <Container display={Container.displays.flex}>
          <Container display={Container.displays.flex} pr="2" width="3/12">
            <Container
              display={Container.displays.flex}
              alignItems={Container.alignItemsValues.center}
              justifyContent={Container.justifyContentValues.center}
              width={['16', '36']}
              height={['16', '36']}>
              {item.image && <LazyImage src={item.image} />}
            </Container>
          </Container>
          <Container
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}
            width="9/12">
            <Container
              display={[
                Container.displays.block,
                Container.displays.block,
                Container.displays.none
              ]}>
              {renderProductItemName(true)}
            </Container>
            <Container
              pb="3"
              display={[
                Container.displays.none,
                Container.displays.none,
                Container.displays.block
              ]}>
              <Container data-testing="Cart_TextTitle_ProductName">
                {renderProductItemName(false)}
              </Container>
            </Container>
            {item.isSample && (
              <Container
                display={Container.displays.flex}
                pt="2"
                pb={['4', '6']}>
                <TextBody
                  {...htmlTranslation(translations, 'tk_sampleFreeSubtitle')}
                />
              </Container>
            )}
            <SelectionAndFreeSample
              item={item}
              selectedVariant={selectedVariant}
              handleQuantityChange={handleQuantityChange}
              handleRemove={handleRemove}
              canQuantityBeChanged={canQuantityBeChanged}
              canOptionBeChanged={canOptionBeChanged}
              canBeRemoved={canBeRemoved}
              handleOptionListChange={handleOptionListChange}
              selectVariantItems={selectVariantItems}
              showInMobile={false}
              translations={translations}
            />
            {renderPromotionBanner(false)}
          </Container>
        </Container>
        <SelectionAndFreeSample
          item={item}
          selectedVariant={selectedVariant}
          handleQuantityChange={handleQuantityChange}
          handleRemove={handleRemove}
          canQuantityBeChanged={canQuantityBeChanged}
          canOptionBeChanged={canOptionBeChanged}
          canBeRemoved={canBeRemoved}
          handleOptionListChange={handleOptionListChange}
          selectVariantItems={selectVariantItems}
          showInMobile={true}
          translations={translations}
        />
        {renderPromotionBanner(true)}
      </Container>
    </ListContainer>
  )
}

export default ProductItem

const SelectionAndFreeSample = ({
  item,
  selectedVariant,
  handleQuantityChange,
  handleRemove,
  canQuantityBeChanged,
  canOptionBeChanged,
  canBeRemoved,
  handleOptionListChange,
  selectVariantItems,
  showInMobile,
  translations
}) => {
  const handleTasteSelector = () => {
    analytics.tasteSelector('View Details - Sampling Page')
    window.dispatchEvent(new window.CustomEvent('showTasteMatchOverlay'))
  }
  return (
    <Container
      display={
        showInMobile
          ? [Container.displays.block, Container.displays.none]
          : [Container.displays.none, Container.displays.block]
      }>
      <SelectionArea
        item={item}
        handleQuantityChange={handleQuantityChange}
        handleRemove={handleRemove}
        canQuantityBeChanged={canQuantityBeChanged}
        canOptionBeChanged={canOptionBeChanged}
        canBeRemoved={canBeRemoved}
        handleOptionListChange={handleOptionListChange}
        selectVariantItems={selectVariantItems}
        translations={translations}
        isMobile={showInMobile}
      />
      {item.isSample &&
        item.isSampleConfirmed &&
        selectedVariant &&
        selectedVariant.additionalAttributes && (
          <Container pt={['6', '8']}>
            <Container
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.between}
              pb="4">
              <TextBody data-testing="Cart_TextBody_ShortDescription">
                {selectedVariant.shortDescription}
              </TextBody>
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.end}
                flexShrink={0}
                pl="8">
                <Link
                  onClickHandler={handleTasteSelector}
                  data-testing="Cart_Link_TasteSelector">
                  {translations.tk_tasteSelectorLabel}
                </Link>
              </Container>
            </Container>
            <Container
              display={Container.displays.flex}
              flexDirection={Container.flexDirections.column}>
              {selectedVariant.additionalAttributes.map((attribute, index) => {
                return attribute.value > 0 ? (
                  <AttributeRating
                    key={index}
                    name={attribute.name}
                    value={attribute.value}
                  />
                ) : null
              })}
            </Container>
          </Container>
        )}
    </Container>
  )
}

const SelectionArea = ({
  item,
  handleQuantityChange,
  handleRemove,
  canQuantityBeChanged,
  canOptionBeChanged,
  canBeRemoved,
  handleOptionListChange,
  selectVariantItems,
  translations,
  isMobile
}) => {
  return (
    <Container
      display={Container.displays.flex}
      flexDirection={Container.flexDirections.row}
      alignItems={Container.alignItemsValues.center}>
      {!item.isSample && (
        <Container width={['4/12', '3/12']} pr={['2', '4']}>
          <Select
            value={String(item.amount)}
            items={item.availableQuantities.map(q => String(q))}
            onChangeHandler={handleQuantityChange}
            disabled={!!item.chainId || !canQuantityBeChanged}
            getItemValue={(index, item) => parseInt(item)}
            getItemLabel={(index, item) => item}
            name="quantity"
            label=""
            fullWidth
            data-testing={`Cart_Select_ProductQuantity${
              isMobile ? 'Mobile' : ''
            }`}
          />
        </Container>
      )}
      {(item.options.length > 1 ||
        (item.isSample && item.options.length > 0)) &&
        canOptionBeChanged && (
          <Container
            fullWidth={item.isSample}
            px="px"
            width={!item.isSample ? ['8/12', '10/12', '6/8'] : null}>
            <Select
              value={
                item.isSample && !item.isSampleConfirmed
                  ? ''
                  : String(item.selectedOptionIndex)
              }
              onChangeHandler={handleOptionListChange}
              items={selectVariantItems}
              getItemValue={(index, item) => parseInt(item.value)}
              getItemLabel={(index, item) => item.label}
              name="variant"
              label=""
              fullWidth
              data-testing={`Cart_Select_SampleType${isMobile ? 'Mobile' : ''}`}
            />
          </Container>
        )}
      {(!item.isSample || item.isSampleConfirmed) && canBeRemoved && (
        <Container width={['3/12']}>
          <Container
            data-testing={`Cart_Overlay_RemoveItem${isMobile ? 'Mobile' : ''}`}
            pl="4">
            <TextBody>
              <Link onClickHandler={handleRemove}>
                {translations.tk_removeButton}
              </Link>
            </TextBody>
          </Container>
        </Container>
      )}
    </Container>
  )
}

const AttributeRating = props => {
  const { name, value } = props
  return (
    <Container
      display={Container.displays.flex}
      alignItems={Container.alignItemsValues.center}>
      <Container width="32">
        <TextBody ellipsis size="xs" data-testing="Cart_TextBody_AttributeName">
          {name}
        </TextBody>
      </Container>
      <Container pl="4">
        <Score
          max={5}
          value={value}
          data-testing="Cart_Score_AttributeRating"
        />
      </Container>
    </Container>
  )
}
